/**
 * Party voice.
 *
 * Peer-to-peer mesh over WebRTC. The server only relays SDP and ICE - audio
 * never touches it. Mesh keeps latency low and needs no media server; party
 * sizes are 2-6 people.
 *
 * Joining never depends on having a microphone. With no mic, or a declined
 * prompt, we connect listen-only: you hear everyone and appear muted. Nobody
 * gets locked out of a call for want of hardware.
 */

import { useSession } from './session';
import { create } from 'zustand';
import { play } from '@/lib/sound';
import { socket } from '@/lib/api';
import type { VoiceMember } from '@/lib/types';
import { bridge } from '@/lib/bridge';

const FALLBACK_ICE: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' }
];

type Peer = {
  pc: RTCPeerConnection;
  audio: HTMLAudioElement;
  pending: RTCIceCandidateInit[];
  /**
   * Who gives way when both ends offer at once.
   *
   * Exactly one side of every pair is polite - the side that did not open the
   * connection - and that is the side that rolls back its own offer and takes
   * the other's. Without it both ends hold their own offer, both reject the
   * other's, and the connection sits in have-local-offer for ever.
   */
  polite: boolean;
  /** True between creating an offer and having it accepted. */
  makingOffer?: boolean;
  stream?: MediaStream;
};

type Share = { userId: string; stream: MediaStream; local: boolean; name?: string };

type VoiceState = {
  partyId: string | null;
  /** Which voice channel of that party you are in. */
  channelId: string | null;
  members: VoiceMember[];
  /** Everyone in every party, with their mute and share state. */
  roomStates: Record<string, VoiceMember[]>;
  /** partyId -> ids in voice there, for every party, not just yours. */
  rooms: Record<string, string[]>;
  /** partyId -> channelId -> ids, so the sidebar can show each room. */
  byChannel: Record<string, Record<string, string[]>>;
  muted: boolean;
  deafened: boolean;
  listenOnly: boolean;
  sharing: boolean;
  screens: Share[];
  /** Chosen microphone and speakers, or '' for whatever the system picked. */
  inputId: string;
  outputId: string;
  /** How loud each person is, for you only. 0 is muted, 1 is as sent. */
  levels: Record<string, number>;
  /** What quality your own screen share is going out at, while it is live. */
  shareQuality: 'smooth' | 'balanced' | 'sharp';

  join: (partyId: string, channelId?: string) => Promise<void>;
  leave: () => Promise<void>;
  toggleMute: (silent?: boolean) => void;
  toggleDeafen: () => void;
  startScreenShare: (quality: 'smooth' | 'balanced' | 'sharp') => Promise<boolean>;
  stopScreenShare: () => void;
  /**
   * Pressing share while already sharing: swap to a different window or
   * screen rather than stopping. Stopping is still one press away from the
   * picker, and swapping used to mean stop-then-start, which dropped the
   * picture for everyone watching in between.
   */
  switchScreenShare: (quality: 'smooth' | 'balanced' | 'sharp') => Promise<boolean>;
  /** Change quality without dropping the share, if the browser allows it. */
  setShareQuality: (quality: 'smooth' | 'balanced' | 'sharp') => Promise<void>;
  setInput: (deviceId: string) => Promise<void>;
  setOutput: (deviceId: string) => Promise<void>;
  /** Your own volume for one person. Does not affect what anyone else hears. */
  setLevel: (userId: string, level: number) => void;
};

/* Kept outside the store: these are live objects, not state React renders. */
const peers = new Map<string, Peer>();
let localStream: MediaStream | null = null;
let screenStream: MediaStream | null = null;
let screenName: string | null = null;
let audioCtx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let raf = 0;
let speaking = false;
let bound = false;
let iceServers: RTCIceServer[] = FALLBACK_ICE;

export function setIceServers(list: RTCIceServer[] | undefined): void {
  if (list?.length) iceServers = list;
}

/**
 * What each quality step actually asks the display for.
 *
 * Smooth trades resolution for frame rate, sharp does the reverse. They are
 * a table rather than three literals so starting a share and re-tuning one
 * mid-call can never drift apart.
 */
/*
 * Raised after review ("better quality, higher quality"). The old steps topped
 * out at 720p60 for motion and 1440p at fifteen frames, and nothing told the
 * encoder how many bits it could spend, so WebRTC's conservative defaults
 * turned text to mush the moment anything moved. Each step now carries a
 * bitrate ceiling, a content hint and what to give up first under pressure.
 */
export const SHARE_PRESETS = {
  /* Games and video: keep the frame rate, drop resolution if the link sags. */
  smooth: { frameRate: 60, height: 1080, bitrate: 8_000_000, hint: 'motion', degrade: 'maintain-framerate' },
  /* Text, code, documents: keep every pixel sharp. */
  balanced: { frameRate: 30, height: 1440, bitrate: 10_000_000, hint: 'detail', degrade: 'maintain-resolution' },
  /* Both at once, for connections that can carry it. */
  sharp: { frameRate: 60, height: 1440, bitrate: 16_000_000, hint: 'motion', degrade: 'balanced' }
} as const;

function shareConstraints(quality: keyof typeof SHARE_PRESETS): MediaTrackConstraints {
  const p = SHARE_PRESETS[quality] ?? SHARE_PRESETS.smooth;
  return {
    frameRate: { ideal: p.frameRate, max: p.frameRate },
    height: { ideal: p.height, max: p.height },
    width: { ideal: Math.round((p.height * 16) / 9), max: Math.round((p.height * 16) / 9) }
  };
}

/**
 * Tells one connection how to send the screen.
 *
 * Content hint first (the encoder treats "detail" and "motion" differently),
 * then the sender's bitrate ceiling, frame-rate cap and degradation choice.
 * Codec preference goes VP9, then H.264, then whatever is left - VP9 holds
 * text far better than VP8 at the same bitrate, and it is set before the
 * offer that carries the track.
 */
function tuneScreenSender(pc: RTCPeerConnection, track: MediaStreamTrack, quality: keyof typeof SHARE_PRESETS): void {
  const p = SHARE_PRESETS[quality] ?? SHARE_PRESETS.smooth;
  try { track.contentHint = p.hint; } catch { /* not supported */ }

  const transceiver = pc.getTransceivers().find((t) => t.sender.track === track);
  if (!transceiver) return;

  try {
    const caps = RTCRtpSender.getCapabilities?.('video');
    if (caps && typeof transceiver.setCodecPreferences === 'function') {
      const rank = (mime: string) => (/vp9/i.test(mime) ? 0 : /h264/i.test(mime) ? 1 : /av1/i.test(mime) ? 2 : 3);
      const sorted = [...caps.codecs].sort((a, b) => rank(a.mimeType) - rank(b.mimeType));
      transceiver.setCodecPreferences(sorted);
    }
  } catch { /* the browser keeps its own order */ }

  const sender = transceiver.sender;
  try {
    const params = sender.getParameters();
    if (!params.encodings || !params.encodings.length) params.encodings = [{}];
    for (const enc of params.encodings) {
      enc.maxBitrate = p.bitrate;
      enc.maxFramerate = p.frameRate;
      enc.scaleResolutionDownBy = 1;
    }
    (params as RTCRtpSendParameters & { degradationPreference?: string }).degradationPreference = p.degrade;
    void sender.setParameters(params).catch(() => { /* applied after negotiation instead */ });
  } catch { /* parameters not ready yet - retried after negotiation */ }
}

/**
 * Points one audio element at a chosen output device.
 *
 * setSinkId is not in every browser and rejects for a device that has gone
 * away, and neither is worth surfacing: the element keeps playing on the
 * system default, which is the right outcome either way.
 */
async function applySink(el: HTMLAudioElement, deviceId: string): Promise<void> {
  const sinkable = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
  if (!deviceId || typeof sinkable.setSinkId !== 'function') return;
  try { await sinkable.setSinkId(deviceId); } catch { /* stays on default */ }
}

const INPUT_KEY = 'pogu.voice.input';
const OUTPUT_KEY = 'pogu.voice.output';
const LEVELS_KEY = 'pogu.voice.levels';

function readLevels(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(LEVELS_KEY) ?? '{}'); }
  catch { return {}; }
}

/**
 * The microphone, on whichever device is chosen.
 *
 * The processing flags stay on whatever the device is: echo cancellation and
 * noise suppression are what make a laptop speaker-and-mic pair usable at all.
 */
function micConstraints(deviceId: string): MediaStreamConstraints {
  return {
    audio: {
      /* Each can be turned off in Voice & video. */
      echoCancellation: useSession.getState().settings?.voiceEcho !== false,
      noiseSuppression: useSession.getState().settings?.voiceNoise !== false,
      autoGainControl: useSession.getState().settings?.voiceAgc !== false,
      ...(deviceId ? { deviceId: { exact: deviceId } } : null)
    },
    video: false
  };
}

/** Lists the microphones and speakers the browser will admit to. */
export async function listDevices(): Promise<{ inputs: MediaDeviceInfo[]; outputs: MediaDeviceInfo[] }> {
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    return {
      inputs: all.filter((d) => d.kind === 'audioinput'),
      // Chrome only exposes output devices once permission is granted, and
      // Firefox not at all - an empty list means "system default only".
      outputs: all.filter((d) => d.kind === 'audiooutput')
    };
  } catch {
    return { inputs: [], outputs: [] };
  }
}

export const useVoice = create<VoiceState>((set, get) => ({
  partyId: null,
  channelId: null,
  members: [],
  roomStates: {},
  rooms: {},
  byChannel: {},
  muted: false,
  deafened: false,
  listenOnly: false,
  sharing: false,
  screens: [],
  inputId: localStorage.getItem(INPUT_KEY) ?? '',
  outputId: localStorage.getItem(OUTPUT_KEY) ?? '',
  levels: readLevels(),
  shareQuality: 'smooth',

  async join(partyId, channelId = 'voice') {
    // Re-joining the channel you are already in is a no-op; switching
    // channels inside the same party tears the old mesh down first.
    if (get().partyId === partyId && get().channelId === channelId) return;
    if (get().partyId) await get().leave();
    bind();

    // A missing microphone is not a reason to stay out of the call.
    let listenOnly = false;
    try {
      localStream = await navigator.mediaDevices.getUserMedia(micConstraints(get().inputId));
      startLevelMeter();
    } catch {
      localStream = null;
      listenOnly = true;
    }

    set({ partyId, channelId, listenOnly, muted: listenOnly, members: [], screens: [] });
    socket.send('voice:join', { partyId, channelId });
    play('voiceOn');
  },

  async leave() {
    const partyId = get().partyId;
    if (!partyId) return;

    socket.send('voice:leave', { partyId });
    for (const [id] of peers) closePeer(id);
    peers.clear();

    stopLevelMeter();
    localStream?.getTracks().forEach((t) => t.stop());
    localStream = null;
    get().stopScreenShare();

    set({ partyId: null, channelId: null, members: [], screens: [], sharing: false, listenOnly: false, muted: false });
    play('voiceOff');
  },

  toggleMute(silent) {
    const next = !get().muted;
    set({ muted: next });
    localStream?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    // Deafening mutes you as a side effect; one cue for one action.
    if (!silent) play(next ? 'mute' : 'unmute');
    const partyId = get().partyId;
    if (partyId) socket.send('voice:mute', { partyId, muted: next });
  },

  toggleDeafen() {
    const next = !get().deafened;
    set({ deafened: next });
    // Undeafening restores each person to the volume you set them at, not
    // to full - a slider you moved is a preference, not a mute.
    for (const [id, peer] of peers) {
      peer.audio.muted = next;
      peer.audio.volume = next ? 0 : (get().levels[id] ?? 1);
    }
    play(next ? 'deafen' : 'undeafen');

    /*
     * Tell everybody, including ourselves.
     *
     * This was missing entirely: deafening turned the sound off locally and
     * stopped there, so the server never learned about it and the roster -
     * which reads its state from the server - showed a deafened person as
     * merely muted. On your own row too, which is what made it look broken
     * rather than merely wrong.
     */
    const partyId = get().partyId;
    if (partyId) socket.send('voice:mute', { partyId, deafened: next });

    // Deafening implies muting: hearing nobody while they still hear you is
    // not what anyone means by it. Muting sends its own half.
    if (next && !get().muted) get().toggleMute(true);
  },

  async startScreenShare(quality) {
    const partyId = get().partyId;
    if (!partyId) return false;

    const preset = SHARE_PRESETS[quality] ?? SHARE_PRESETS.smooth;
    set({ shareQuality: quality });

    // The desktop app has no built-in picker, so the renderer draws one and
    // tells the main process which source the answer refers to. Without a
    // display-media handler in main, getDisplayMedia never resolves at all -
    // which is exactly what "screen share does nothing" looks like.
    const api = bridge();
    let source: { id: string | null; name: string } | null = { id: null, name: 'Screen' };
    if (api?.screenSources) {
      const { pickScreenSource } = await import('@/components/ScreenPicker');
      source = await pickScreenSource();
      if (!source) return false;
      if (source.id) await api.pickScreen(source.id);
    }

    try {
      if (api?.screenSources && source.id) {
        screenStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            // @ts-expect-error - Electron's desktop capture constraints
            mandatory: {
              chromeMediaSource: 'desktop',
              chromeMediaSourceId: source.id,
              minFrameRate: Math.min(30, preset.frameRate),
              maxFrameRate: preset.frameRate,
              maxHeight: preset.height,
              maxWidth: Math.round((preset.height * 16) / 9)
            }
          }
        });
      } else {
        screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: shareConstraints(quality),
          audio: false
        });
      }
    } catch (err) {
      screenStream = null;
      const e = err as Error;
      if (e.name !== 'NotAllowedError' && e.name !== 'AbortError') throw err;
      return false;
    }

    const track = screenStream.getVideoTracks()[0];
    if (!track) { screenStream = null; return false; }
    track.addEventListener('ended', () => get().stopScreenShare());

    screenName = source.name;
    for (const [userId, peer] of peers) {
      try {
        peer.pc.addTrack(track, screenStream);
        tuneScreenSender(peer.pc, track, quality);
        void renegotiate(userId, peer).then(() => tuneScreenSender(peer.pc, track, quality));
      } catch { /* already added */ }
    }

    socket.send('voice:screen', { partyId, sharing: true });
    set({ sharing: true });
    play('shareStart');
    refreshScreens(set, get);
    return true;
  },

  /**
   * Switch microphone mid-call.
   *
   * The new track is swapped into every existing peer connection rather than
   * renegotiating, so nobody hears a gap and the call does not re-handshake.
   */
  async setInput(deviceId) {
    localStorage.setItem(INPUT_KEY, deviceId);
    set({ inputId: deviceId });
    if (!get().partyId) return;

    let next: MediaStream;
    try { next = await navigator.mediaDevices.getUserMedia(micConstraints(deviceId)); }
    catch { return; }

    const track = next.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !get().muted;

    for (const peer of peers.values()) {
      const sender = peer.pc.getSenders().find((sn) => sn.track?.kind === 'audio');
      if (sender) void sender.replaceTrack(track);
    }
    localStream?.getTracks().forEach((t) => t.stop());
    localStream = next;
    startLevelMeter();
  },

  /**
   * Send everyone else's audio to a chosen output.
   *
   * setSinkId is Chromium-only; where it is missing the browser keeps using
   * the system default, which is why this never reports an error.
   */
  async setOutput(deviceId) {
    localStorage.setItem(OUTPUT_KEY, deviceId);
    set({ outputId: deviceId });
    for (const peer of peers.values()) await applySink(peer.audio, deviceId);
  },

  setLevel(userId, level) {
    const next = { ...get().levels, [userId]: level };
    set({ levels: next });
    try { localStorage.setItem(LEVELS_KEY, JSON.stringify(next)); } catch { /* storage off */ }
    const peer = peers.get(userId);
    if (peer) peer.audio.volume = get().deafened ? 0 : level;
  },

  async switchScreenShare(quality) {
    if (!get().sharing) return get().startScreenShare(quality);
    // Stop first: a second capture of the same display while the first is
    // still running is refused on Windows, and the picker needs the old one
    // gone before it can offer that source again.
    get().stopScreenShare();
    return get().startScreenShare(quality);
  },

  async setShareQuality(quality) {
    set({ shareQuality: quality });
    const track = screenStream?.getVideoTracks()[0];
    if (!track) return;
    // applyConstraints re-negotiates nothing: the same track simply starts
    // producing a different size, so the people watching never lose the feed.
    try { await track.applyConstraints(shareConstraints(quality)); }
    catch { /* the display source refused - it keeps its current size */ }
    // And the encoder follows, so a sharper picture gets the bits to carry it.
    for (const peer of peers.values()) tuneScreenSender(peer.pc, track, quality);
  },

  stopScreenShare() {
    if (!screenStream) return;
    for (const [userId, peer] of peers) {
      let changed = false;
      for (const sender of peer.pc.getSenders()) {
        if (sender.track?.kind === 'video') {
          try { peer.pc.removeTrack(sender); changed = true; } catch { /* closing */ }
        }
      }
      if (changed) void renegotiate(userId, peer);
    }
    screenStream.getTracks().forEach((t) => t.stop());
    screenStream = null;
    screenName = null;

    const partyId = get().partyId;
    if (partyId) socket.send('voice:screen', { partyId, sharing: false });
    set({ sharing: false });
    refreshScreens(set, get);
  }
}));

/* ------------------------------------------------------- signalling */

/**
 * Subscribes to the voice feed.
 *
 * Called once at start-up, not on join. It used to run only inside join(),
 * so until you were sitting in a call yourself the app had never subscribed
 * to voice:state at all - which is why nobody in the roster ever showed as
 * muted, deafened or sharing. The mesh handlers below all check partyId, so
 * listening early costs nothing.
 */
export function wireVoice(): void { bind(); }

function bind(): void {
  if (bound) return;
  bound = true;
  const { getState: get, setState: set } = useVoice;

  socket.on('voice:peers', (data: { partyId: string; peers: { userId: string; initiate: boolean }[] }) => {
    if (data.partyId !== get().partyId) return;
    for (const p of data.peers) void connectTo(p.userId, p.initiate);
  });

  socket.on('voice:peer_joined', (data: { partyId: string; userId: string; initiate: boolean }) => {
    if (data.partyId !== get().partyId) return;
    void connectTo(data.userId, data.initiate);
  });

  socket.on('voice:peer_left', (data: { partyId: string; userId: string }) => {
    if (data.partyId !== get().partyId) return;
    closePeer(data.userId);
    set({ members: get().members.filter((m) => m.userId !== data.userId) });
    refreshScreens(set, get);
  });

  socket.on('voice:signal', (data: { partyId: string; from: string; kind: string; payload: any }) => {
    if (data.partyId !== get().partyId) return;
    void onSignal(data);
  });

  // Kept for every party, not just the one you are in. Reading it only while
  // connected is what left people showing as "in voice" after they had left.
  socket.on('voice:state', (data: {
    partyId: string;
    members: VoiceMember[];
    byChannel?: Record<string, string[]>;
  }) => {
    set({
      rooms: { ...get().rooms, [data.partyId]: data.members.map((m) => m.userId) },
      byChannel: { ...get().byChannel, [data.partyId]: data.byChannel ?? {} },
      // Whether somebody is muted is worth showing whether or not you are in
      // the channel with them - the roster used to lose that the moment you
      // were not connected, so nobody ever had an icon.
      roomStates: { ...get().roomStates, [data.partyId]: data.members }
    });
    // Only the channel you are actually in drives the local mesh view.
    if (data.partyId === get().partyId) {
      const mine = get().channelId;
      set({ members: data.members.filter((m) => !mine || m.channelId === mine) });

      /*
       * Take down a screen the server says is no longer being shared.
       *
       * The only signal for this was the remote track firing 'ended', and
       * removeTrack does not fire it - it mutes the track instead. So a share
       * that stopped left a frozen last frame on everybody else's stage until
       * they left the call. The server already knows who is sharing, because
       * it tracks it to draw the roster icon; this just believes it.
       */
      let dropped = false;
      const sharing = new Set(data.members.filter((m) => m.sharing).map((m) => m.userId));
      for (const userId of [...remoteScreens.keys()]) {
        if (!sharing.has(userId)) { remoteScreens.delete(userId); dropped = true; }
      }
      if (dropped) refreshScreens(set, get);
    }
  });

  socket.on('voice:speaking', (data: { partyId: string; userId: string; speaking: boolean }) => {
    if (data.partyId !== get().partyId) return;
    set({
      members: get().members.map((m) => (m.userId === data.userId ? { ...m, speaking: data.speaking } : m))
    });
  });
}

async function connectTo(userId: string, initiate: boolean): Promise<void> {
  const { getState: get, setState: set } = useVoice;
  if (peers.has(userId)) return;

  const pc = new RTCPeerConnection({ iceServers });
  const audio = new Audio();
  audio.autoplay = true;
  audio.muted = get().deafened;
  // Your own volume for this person, and your chosen speakers. Both are
  // local: nobody else hears the difference.
  audio.volume = get().deafened ? 0 : (get().levels[userId] ?? 1);
  void applySink(audio, get().outputId);

  const peer: Peer = { pc, audio, pending: [], polite: !initiate };
  peers.set(userId, peer);

  if (localStream) {
    for (const track of localStream.getTracks()) pc.addTrack(track, localStream);
  } else {
    // Listen-only: no track to send, but we still need an audio m-line.
    pc.addTransceiver('audio', { direction: 'recvonly' });
  }
  if (screenStream) {
    for (const track of screenStream.getTracks()) {
      pc.addTrack(track, screenStream);
      tuneScreenSender(pc, track, get().shareQuality);
    }
  }

  pc.ontrack = (ev) => {
    if (ev.track.kind === 'video') {
      const stream = ev.streams[0];
      if (stream) {
        remoteScreens.set(userId, stream);
        ev.track.addEventListener('ended', () => { remoteScreens.delete(userId); refreshScreens(set, get); });
        refreshScreens(set, get);
      }
      return;
    }
    peer.stream = ev.streams[0];
    audio.srcObject = ev.streams[0] ?? null;
    void audio.play().catch(() => { /* autoplay policy - resolves on a gesture */ });
  };

  /*
   * Anything that changes the session - adding a screen track, dropping one -
   * fires this on the side that changed. Both sides are allowed to offer now,
   * because onSignal resolves a collision properly; the old guard here let
   * only the opener renegotiate, so a share started by the person who
   * answered the call was never offered to anybody.
   */
  pc.onnegotiationneeded = () => { void renegotiate(userId, peer); };

  pc.onicecandidate = (ev) => {
    if (!ev.candidate) return;
    socket.send('voice:signal', {
      partyId: get().partyId, to: userId, kind: 'ice', payload: ev.candidate.toJSON()
    });
  };

  pc.onconnectionstatechange = () => {
    if (pc.connectionState === 'failed') { closePeer(userId); void connectTo(userId, initiate); }
  };

  if (initiate) await renegotiate(userId, peer);
}

/**
 * Adding or dropping a track changes the session description and both sides
 * have to agree on the new one. onnegotiationneeded only fires for the peer
 * that opened the connection, so the answering side offers from here -
 * otherwise a share started by the second person is never seen by anyone.
 */
async function renegotiate(userId: string, peer: Peer): Promise<void> {
  if (peer.pc.signalingState !== 'stable') return;
  try {
    peer.makingOffer = true;
    const offer = await peer.pc.createOffer();
    if (peer.pc.signalingState !== 'stable') return;
    await peer.pc.setLocalDescription(offer);
    socket.send('voice:signal', {
      partyId: useVoice.getState().partyId, to: userId, kind: 'offer', payload: offer
    });
  } catch { /* the other side retries */ }
  finally { peer.makingOffer = false; }
}

async function onSignal(data: { from: string; kind: string; payload: any }): Promise<void> {
  const peer = peers.get(data.from);
  if (!peer) {
    if (data.kind === 'offer') await connectTo(data.from, false);
    else return;
  }
  const entry = peers.get(data.from);
  if (!entry) return;

  try {
    if (data.kind === 'offer') {
      /*
       * Both ends offering at once.
       *
       * Starting a screen share adds a track and offers; if the other end is
       * mid-offer of its own, setRemoteDescription throws InvalidStateError -
       * and that throw used to land in the catch at the bottom of this
       * function and be forgotten, with nothing retrying. The share was
       * negotiated exactly once, it failed, and nobody ever saw the screen.
       *
       * The rule is the standard one: the impolite side keeps its own offer
       * and ignores the incoming one, whose sender will re-offer once ours
       * lands. The polite side rolls its own offer back and takes theirs.
       */
      const collision = !!entry.makingOffer || entry.pc.signalingState !== 'stable';
      if (collision && !entry.polite) return;

      if (collision) {
        await Promise.all([
          entry.pc.setLocalDescription({ type: 'rollback' } as RTCSessionDescriptionInit),
          entry.pc.setRemoteDescription(new RTCSessionDescription(data.payload))
        ]);
      } else {
        await entry.pc.setRemoteDescription(new RTCSessionDescription(data.payload));
      }

      for (const c of entry.pending.splice(0)) await entry.pc.addIceCandidate(c);
      const answer = await entry.pc.createAnswer();
      await entry.pc.setLocalDescription(answer);
      socket.send('voice:signal', {
        partyId: useVoice.getState().partyId, to: data.from, kind: 'answer', payload: answer
      });
    } else if (data.kind === 'answer') {
      if (entry.pc.signalingState === 'have-local-offer') {
        await entry.pc.setRemoteDescription(new RTCSessionDescription(data.payload));
        for (const c of entry.pending.splice(0)) await entry.pc.addIceCandidate(c);
      }
    } else if (data.kind === 'ice') {
      if (entry.pc.remoteDescription) await entry.pc.addIceCandidate(data.payload);
      else entry.pending.push(data.payload);
    }
  } catch { /* a failed peer is retried by onconnectionstatechange */ }
}

function closePeer(userId: string): void {
  const peer = peers.get(userId);
  if (!peer) return;
  try { peer.pc.close(); } catch { /* already closed */ }
  peer.audio.srcObject = null;
  peers.delete(userId);
  remoteScreens.delete(userId);
}

/* ---------------------------------------------------------- screens */

const remoteScreens = new Map<string, MediaStream>();

/**
 * Everything on the stage. Your own share goes first - without it the person
 * sharing sees nothing at all and has no idea it is working.
 */
function refreshScreens(
  set: (partial: Partial<VoiceState>) => void,
  get: () => VoiceState
): void {
  const list: Share[] = [...remoteScreens.entries()].map(([userId, stream]) => ({
    userId, stream, local: false
  }));
  if (screenStream) {
    list.unshift({
      userId: 'me',
      stream: screenStream,
      local: true,
      name: screenName ?? 'Your screen'
    });
  }
  set({ screens: list });
  void get;
}

/* ------------------------------------------------------ level meter */

function startLevelMeter(): void {
  if (!localStream) return;
  try {
    audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(localStream);
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.75;
    source.connect(analyser);
  } catch {
    return;
  }

  const buf = new Uint8Array(analyser.frequencyBinCount);
  const tick = () => {
    if (!analyser) return;
    analyser.getByteFrequencyData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
    const rms = Math.sqrt(sum / buf.length) / 255;

    // Only the speaking state leaves this loop. Nothing shows a raw level -
    // a twitching meter is noise, a glowing avatar is the point.
    const next = !useVoice.getState().muted && rms > 0.055;
    if (next !== speaking) {
      speaking = next;
      const partyId = useVoice.getState().partyId;
      if (partyId) socket.send('voice:speaking', { partyId, speaking: next });
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
}

function stopLevelMeter(): void {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
  analyser = null;
  void audioCtx?.close().catch(() => {});
  audioCtx = null;
  speaking = false;
}
